import { useState, useRef, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";

/**
 * Shared ride-chat logic for passenger and driver dashboards:
 * message list, unread badge, open/close panel and incoming WS chat handling.
 * `sendChat` stays in the component because it needs the WS `send` function.
 */
export function useRideChat({ activeRide, userId }) {
  const [messages, setMessages] = useState([]);
  const [showChat, setShowChat] = useState(false);
  const [unread, setUnread] = useState(0);
  const chatOpenRef = useRef(false);

  useEffect(() => {
    chatOpenRef.current = showChat;
    if (showChat) setUnread(0);
  }, [showChat]);

  const openChat = useCallback(async () => {
    if (!activeRide) return;
    try {
      const { data } = await api.get(`/rides/${activeRide.id}/messages`);
      setMessages(data || []);
    } catch (err) {
      logger.error("[chat] load messages failed", err);
    }
    setShowChat(true);
  }, [activeRide]);

  // Append an incoming WS chat message (dedup) and bump unread when panel is closed.
  const handleChatMessage = useCallback((msg) => {
    setMessages((m) => (m.some((x) => x.id === msg.message.id) ? m : [...m, msg.message]));
    if (!chatOpenRef.current && msg.message.sender_id !== userId) setUnread((u) => u + 1);
  }, [userId]);

  const resetChat = useCallback(() => {
    setShowChat(false);
    setMessages([]);
  }, []);

  return { messages, showChat, setShowChat, unread, openChat, handleChatMessage, resetChat };
}
